'use client';

import { useControllableState } from '@bgzt/hooks';
import clsx from 'clsx';
import { motion, useAnimation } from 'motion/react';
import { useEffect, useRef } from 'react';
import styles from './ImageCarousel.module.scss';

const MotionDiv = motion.div;

type Props = {
  data: string[];
  index?: number;
  defaultIndex?: number;
  onChange?: (index: number) => void;
  children?: (src: string, index: number) => React.ReactNode;
  wrapperClassNames?: string | string[];
  imageWrapperClassNames?: string | string[];
  autoplay?: boolean;
};

export const ImageCarousel = ({
  data,
  index: indexProp,
  defaultIndex = 0,
  children,
  onChange,
  wrapperClassNames,
  imageWrapperClassNames,
  autoplay = false,
}: Props) => {
  const controls = useAnimation();

  const [index = defaultIndex, setIndex] = useControllableState({
    prop: indexProp,
    onChange,
    defaultProp: defaultIndex,
  });

  /** 현재 이미지와 스와이프시 양 옆으로 보여져야 하는 이미지 index */
  const currentImageIndexes = [(index - 1 + data.length) % data.length, index, (index + 1) % data.length];
  /** key로 쓰일 index (이미지가 2개인 경우 currentImageIndexes에서는 중복되는 key 발생하므로 따로 만들어줌) */
  const keys = data.length === 2 ? (index === 0 ? ['-1', '0', '1'] : ['-0', '1', '0']) : currentImageIndexes;

  const wasDragged = useRef(false);

  useEffect(() => {
    if (!autoplay) return;

    /** 왼쪽 방향으로 스와이프 */
    const leftSwipe = async () => {
      const newIndex = (index + 1 + data.length) % data.length;
      // 스와이프 애니메이션
      await controls.start({ x: '-200%', transition: { duration: 0.3 } });
      // index 변경
      setIndex(newIndex);
      // index가 변경되면 애니메이션 초기화
      await controls.start({ x: '-100%', transition: { duration: 0 } });
    };

    const timer = setInterval(() => {
      leftSwipe();
    }, 3500);

    return () => {
      clearInterval(timer);
    };
  }, [index, autoplay, data.length, controls, setIndex]);

  if (data.length === 0) return;

  if (data.length === 1) {
    return (
      <div className={clsx([styles.wrapper, wrapperClassNames])}>
        <figure className={clsx([styles.image, imageWrapperClassNames])}>{children?.(data[0], 0)}</figure>
      </div>
    );
  }

  return (
    <MotionDiv
      drag="x"
      animate={controls}
      onDragEnd={async (event, info) => {
        wasDragged.current = true;
        if (
          // 전체 면적의 1/3 이하로 드래그하거나
          Math.abs(info.offset.x) < (event.target as HTMLElement).clientWidth / 3 &&
          // 속도가 300 이하일 경우
          Math.abs(info.velocity.x) < 300
        ) {
          // 원래 위치로 복귀
          await controls.start({ x: '-100%', transition: { duration: 0.3 } });
          return;
        }

        /** 스와이프 방향 (-1인 경우 왼쪽, 1인 경우 오른쪽) */
        const direction = info.offset.x > 0 ? -1 : 1;
        /** 새롭게 보여질 이미지의 index */
        const newIndex = (index + direction + data.length) % data.length;

        // 스와이프 애니메이션
        await controls.start({ x: `${-100 * (direction + 1)}%` });
        // index 변경
        setIndex(newIndex);
        // index가 변경되면 애니메이션 초기화
        await controls.start({ x: '-100%', transition: { duration: 0 } });
      }}
      onClick={(event) => {
        // 상위 요소에 click 이벤트가 있을 경우 스와이프 종료 후 click 이벤트가 실행되어
        // 스와이프 직후 click 이벤트 버블링을 막는다
        if (!wasDragged.current) return;
        event.stopPropagation();
        wasDragged.current = false;
      }}
      style={{ x: '-100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={clsx([styles.wrapper, wrapperClassNames])}
    >
      {keys.map((key) => {
        const index = Math.abs(Number(key));
        return (
          <figure key={key} data-key={key} className={clsx([styles.image, imageWrapperClassNames])}>
            {children?.(data[index], index)}
          </figure>
        );
      })}
    </MotionDiv>
  );
};
